// HIDE-CONNECTOR GUARD (Phase 1.5 of PLAN-pedigree-undo-redo-and-saved-views v1.1,
// §3.5). Hiding a person removes them from the layout input entirely
// (canvasMembers = memberRecords.filter(!hidden)), so hiding a STRUCTURAL
// CONNECTOR — a person whose body is the only linkage between a branch and the
// rest of the family — leaves that branch floating as a disconnected island, and
// the fragmented state persists through the people-save. This analyzer answers,
// BEFORE the hide is applied: "who would be left with no drawn connection to the
// rest of the pedigree?"
//
// Connectivity model = EXACTLY what the renderer draws (layout.js), never more:
//   - marriage edges (spouseIds; crossLevelSpouseIds is a subset but included
//     defensively),
//   - child drop lines, driven by the CHILD's OWN back-pointers only:
//       · both parents visible → edges to both, but ONLY when the parents are
//         each other's spouses (the union-node rule — layout.js draws the
//         both-parents drop from the parents' union, which exists only for
//         spoused couples; unmarried co-parents get NO drawn line),
//       · exactly one parent visible → the single-parent drop line to it.
//   A parent's childrenIds array alone is deliberately NOT an edge: the
//   renderer never draws from it, so counting it would make the guard MORE
//   permissive than the chart (a phantom edge on drifted childrenIds data
//   would declare a fragmenting hide "safe" — extra edges in a guard fail
//   open, not safe). Sibling co-membership is likewise not an edge.
//
// Robust to a pre-fragmented chart: orphans are people reachable from the anchor
// BEFORE the hide but not AFTER — members already floating (legacy hidden sets
// predate this guard) are never attributed to the new hide.

// Sort comparator safe for mixed pid types (legacy string pids, 'dummy-N'
// fallback nodes): numeric-aware string compare, never NaN-inconsistent.
function pidCompare(a, b) {
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

function addEdge(adjacency, a, b) {
  if (a == null || b == null || a === b) return;
  if (!adjacency.has(a) || !adjacency.has(b)) return; // both endpoints visible
  adjacency.get(a).add(b);
  adjacency.get(b).add(a);
}

function areSpouses(a, b) {
  const aList = Array.isArray(a?.spouseIds) ? a.spouseIds : [];
  const bList = Array.isArray(b?.spouseIds) ? b.spouseIds : [];
  return aList.includes(b?.pid) || bList.includes(a?.pid);
}

function buildVisibleAdjacency(visibleMembers) {
  const adjacency = new Map(visibleMembers.map((m) => [m.pid, new Set()]));
  const byPid = new Map(visibleMembers.map((m) => [m.pid, m]));
  for (const m of visibleMembers) {
    const father = m.fatherId != null ? byPid.get(m.fatherId) : undefined;
    const mother = m.motherId != null ? byPid.get(m.motherId) : undefined;
    if (father && mother) {
      // Union rule: the both-parents drop line hangs from the parents' union,
      // which the renderer creates only for spoused couples.
      if (areSpouses(father, mother)) {
        addEdge(adjacency, m.pid, father.pid);
        addEdge(adjacency, m.pid, mother.pid);
      }
    } else if (father) {
      addEdge(adjacency, m.pid, father.pid); // single-parent drop line
    } else if (mother) {
      addEdge(adjacency, m.pid, mother.pid);
    }
    for (const s of Array.isArray(m.spouseIds) ? m.spouseIds : []) {
      addEdge(adjacency, m.pid, s);
    }
    for (const s of Array.isArray(m.crossLevelSpouseIds)
      ? m.crossLevelSpouseIds
      : []) {
      addEdge(adjacency, m.pid, s);
    }
  }
  return adjacency;
}

// BFS over the adjacency from `startPid`, optionally treating `excludedPid` as
// removed from the graph. Returns the reached pid Set (never contains excludedPid).
function reachableFrom(adjacency, startPid, excludedPid = null) {
  const reached = new Set();
  if (
    startPid == null ||
    startPid === excludedPid ||
    !adjacency.has(startPid)
  ) {
    return reached;
  }
  const queue = [startPid];
  reached.add(startPid);
  while (queue.length) {
    const pid = queue.pop();
    for (const next of adjacency.get(pid)) {
      if (next === excludedPid || reached.has(next)) continue;
      reached.add(next);
      queue.push(next);
    }
  }
  return reached;
}

// Largest connected component of the graph with `excludedPid` removed,
// considering only pids in `candidatePids`. Deterministic tie-break: the
// component containing the smallest pid wins.
function largestComponent(adjacency, candidatePids, excludedPid) {
  const remaining = new Set(
    [...candidatePids].filter((p) => p !== excludedPid)
  );
  let best = new Set();
  const orderedSeeds = [...remaining].sort(pidCompare);
  for (const seed of orderedSeeds) {
    if (!remaining.has(seed)) continue;
    const component = reachableFrom(adjacency, seed, excludedPid);
    for (const p of component) remaining.delete(p);
    if (component.size > best.size) best = component;
  }
  return best;
}

/**
 * Analyze hiding `pidToHide` from `members` (the full memberRecords array,
 * hidden included). Returns:
 *   { orphanedPids: [Number] } — the visible people (excluding the target) who
 *   were connected to the pedigree's anchor before the hide but would not be
 *   after. Empty array = the hide is safe.
 *
 * Anchor = the proband (pid 1 / proBandId). When the proband is the hide target
 * or is itself hidden, the anchor is the largest surviving component instead —
 * "keep the biggest coherent chart, flag the rest".
 */
export function analyzeHide(members, pidToHide) {
  const visible = (members || []).filter((m) => m && !m.hidden);
  const visiblePids = new Set(visible.map((m) => m.pid));
  if (!visiblePids.has(pidToHide)) return { orphanedPids: [] };

  const adjacency = buildVisibleAdjacency(visible);
  const proband = visible.find((m) => m.pid === 1 || m.proBandId);
  const probandPid = proband ? proband.pid : null;

  // Reachable BEFORE the hide, from the anchor.
  const anchoredBefore =
    probandPid != null
      ? reachableFrom(adjacency, probandPid)
      : largestComponent(adjacency, visiblePids, null);

  // The hide target wasn't part of the coherent chart to begin with — hiding a
  // member of an already-floating island can't orphan anyone new.
  if (!anchoredBefore.has(pidToHide)) return { orphanedPids: [] };

  // Reachable AFTER the hide. If the anchor IS the target (hiding the proband),
  // keep the largest surviving component.
  const keptAfter =
    probandPid != null && probandPid !== pidToHide
      ? reachableFrom(adjacency, probandPid, pidToHide)
      : largestComponent(adjacency, anchoredBefore, pidToHide);

  const orphanedPids = [];
  for (const pid of anchoredBefore) {
    if (pid !== pidToHide && !keptAfter.has(pid)) orphanedPids.push(pid);
  }
  orphanedPids.sort(pidCompare);
  return { orphanedPids };
}
